import React, { useState, useEffect } from "react";
import { useFaqData } from "../hooks/useFaqData";
import RoundRectButton from "../customized/RoundRectButton";
import { trackPageView, getSessionId, getUserId } from "../utils/pageTracking";
import { useImages } from "../hooks/useImages";

interface ExpandedState {
    [key: string]: boolean;
}
const GUIDS = {
  faqPicGuid: 'b7e8730a-a7df-43a0-96da-fd5564af4b31',
};

const FAQs: React.FC = () => {
    const { faqSections, error } = useFaqData();
    const [expanded, setExpanded] = useState<ExpandedState>({});
 
    const faqImage = useImages(GUIDS.faqPicGuid)?.image;

    useEffect(() => {
        // Track page view
        trackPageView('FAQs', getSessionId(), getUserId());
        
        // Scroll to top when page loads
        window.scrollTo(0, 0);
    }, []);

    const toggle = (section: string, index: number): void => {
        setExpanded((prev) => ({
            ...prev,
            [`${section}-${index}`]: !prev[`${section}-${index}`],
        }));
    };

    // Group FAQs by page
    const groupedFaqs = faqSections.reduce((groups, faq) => {
        const page = faq.page || 'General';
        if (!groups[page]) {
            groups[page] = [];
        }
        groups[page].push(faq);
        return groups;
    }, {} as Record<string, typeof faqSections>);

    return (
        <section className="pt-16 min-h-screen bg-gray-100 text-gray-800">
            <div
                className="relative min-h-[30vh] sm:min-h-[40vh] md:min-h-[50vh] bg-cover bg-center flex items-center justify-center"
                style={{
                    backgroundImage: faqImage ? `url(${faqImage})` : undefined,
                    backgroundPosition: "center center"
                }}
            >
                <div className="absolute inset-0 bg-black opacity-50" />
                <div className="relative z-10">
                    <h1 className="text-4xl sm:text-5xl md:text-6xl font-bold text-orange-600 text-center">
                        Frequently Asked Questions
                    </h1>
                </div>
            </div>


            <div className="mx-auto max-w-5xl px-4 py-16">

                {error && (
                    <div className="mb-8 rounded-lg bg-red-100 border border-red-400 px-4 py-3 text-red-700">
                        <p>{error}</p>
                    </div>
                )}
                {faqSections.length === 0 && !error ? (
                    <div className="text-center text-lg text-gray-600">
                        <p>Loading FAQs...</p>
                    </div>
                ) : faqSections.length === 0 ? (
                    <div className="text-center">
                        <p className="text-xl text-gray-600">No FAQs Available</p>
                        <p className="mt-2 text-gray-500">Please check back later.</p>
                    </div>
                ) : (
                    <>
                        {/* Table of Contents */}
                        {Object.keys(groupedFaqs).length > 1 && (
                            <div className="mb-8 rounded-lg bg-white p-6 shadow-sm border border-gray-200">
                                <h3 className="text-lg font-semibold text-gray-900 mb-4">Quick Navigation</h3>
                                <div className="flex flex-wrap gap-2">
                                    {Object.keys(groupedFaqs).map((page) => (
                                        <button
                                            key={page}
                                            onClick={() => {
                                                const element = document.getElementById(`section-${page}`);
                                                element?.scrollIntoView({ behavior: 'smooth' });
                                            }}
                                            className="px-3 py-1 text-sm  text-white rounded-full hover:bg-green-600 transition-colors"
                                        >
                                            {page}
                                        </button>
                                    ))}
                                </div>
                            </div>
                        )}

                        <div className="space-y-8">
                            {Object.entries(groupedFaqs).map(([page, faqs]) => (
                                <div key={page} id={`section-${page}`} className="space-y-4">
                                    {/* <h3 className="text-2xl font-bold text-gray-900 border-b-2 border-WeaversGreen pb-2">
                                        {page}
                                    </h3> */}
                                    <div className="space-y-4">
                                        {faqs.map((faq, index) => {
                                            const isExpanded = expanded[`${page}-${index}`] || false;

                                            return (
                                                <div
                                                    key={faq.id}
                                                    className="overflow-hidden rounded-lg border border-gray-200 bg-white shadow-sm"
                                                >
                                                    <button
                                                        onClick={() => toggle(page, index)}
                                                        className="flex w-full items-center justify-between px-6 py-4 text-left hover:bg-gray-50 focus:outline-none focus:ring-2 focus:ring-blue-500"
                                                    >
                                                        <h4 className="text-lg font-semibold text-gray-900">
                                                            {faq.heading}
                                                        </h4>
                                                        <svg
                                                            className={`h-5 w-5 transform transition-transform duration-200 ${isExpanded ? "rotate-180" : ""
                                                                }`}
                                                            fill="none"
                                                            viewBox="0 0 24 24"
                                                            stroke="currentColor"
                                                        >
                                                            <path
                                                                strokeLinecap="round"
                                                                strokeLinejoin="round"
                                                                strokeWidth={2}
                                                                d="M19 9l-7 7-7-7"
                                                            />
                                                        </svg>
                                                    </button>

                                                    {isExpanded && (
                                                        <div className="border-t border-gray-200 px-6 py-4">
                                                            <div className="prose max-w-none text-gray-700">
                                                                <p className="whitespace-pre-line">{faq.content}</p>
                                                            </div>
                                                        </div>
                                                    )}
                                                </div>
                                            );
                                        })}
                                    </div>
                                </div>
                            ))}
                        </div>
                    </>
                )}
            </div>
        </section>
    );
};

export default FAQs;